/**
 * Info Tooltip - Shows explanation popup for charts and metrics.
 *
 * Uses createPortal to render the popup into document.body,
 * completely outside the component tree. This avoids all CSS
 * stacking context / overflow / transform issues.
 *
 * Position is computed synchronously in the click handler
 * from the button's getBoundingClientRect().
 */
import { createPortal } from 'preact/compat';
import { useEffect, useRef, useState } from 'preact/hooks';
import { CHART_INFO } from './chartTheme.js';
import { Icons } from './Icons.jsx';
import { IconInfo } from '../../../shared/Icons.jsx';

const POPUP_WIDTH = 300;

export function InfoTooltip({ infoKey, title: titleProp, description: descProp }) {
  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const popupRef = useRef(null);

  const info = infoKey ? CHART_INFO[infoKey] : null;
  const title = titleProp || info?.title;
  const description = descProp || info?.description;
  if (!title && !description) return null;

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();

      // Center popup horizontally below the button
      let left = rect.left + rect.width / 2 - POPUP_WIDTH / 2;
      left = Math.max(8, Math.min(left, window.innerWidth - POPUP_WIDTH - 8));

      // Place below button; if it would overflow viewport bottom, place above
      let top = rect.bottom + 8;
      if (top + 220 > window.innerHeight) {
        top = rect.top - 8 - 220;
        if (top < 8) top = 8;
      }

      setPos({ top, left });
    }
    setIsOpen((v) => !v);
  };

  // Close on outside click, scroll, or resize
  useEffect(() => {
    if (!isOpen) return;

    const close = () => setIsOpen(false);

    const handleClickOutside = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        popupRef.current && !popupRef.current.contains(e.target)
      ) {
        close();
      }
    };

    // Use setTimeout so the current click event doesn't immediately close it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [isOpen]);

  return (
    <span className="chart-info-wrapper">
      <button
        ref={btnRef}
        className="chart-info-btn"
        onClick={handleToggle}
        title="Was bedeutet das?"
        type="button"
      >
        ?
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={popupRef}
            className="chart-info-popup"
            style={{
              position: 'fixed',
              top: `${pos.top}px`,
              left: `${pos.left}px`,
              width: `${POPUP_WIDTH}px`,
              zIndex: 99999,
            }}
          >
            <div className="chart-info-popup-header">
              <span className="chart-info-popup-icon"><IconInfo size={16} /></span>
              <h4>{title}</h4>
              <button
                className="chart-info-popup-close"
                onClick={() => setIsOpen(false)}
                type="button"
              >
                {Icons.x}
              </button>
            </div>
            <p>{description}</p>
          </div>,
          document.body
        )}
    </span>
  );
}
